import { integrationApi, type Project } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { matchingSearchConsoleSite } from '@/lib/growth-signals';
import { useFormError } from '@/hooks/use-form-errors';

const readOAuthResult = () => {
  const params = new URLSearchParams(window.location.hash.slice(1));
  return {
    ticket: params.get('gsc_ticket'),
    error: params.get('gsc_error'),
  };
};

const clearOAuthResult = () => {
  if (!window.location.hash) {
    return;
  }
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}`,
  );
};

export const ConnectSearchConsoleDialog = ({
  project,
  open,
  onOpenChange,
  returnTo,
}: {
  project: Project;
  open: boolean;
  returnTo?: 'setup';
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [ticket, setTicket] = React.useState<string | null>(null);
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const [siteUrl, setSiteUrl] = React.useState('');

  React.useEffect(() => {
    const result = readOAuthResult();
    if (result.ticket || result.error) {
      setTicket(result.ticket);
      setOauthError(result.error);
      onOpenChange(true);
    }
  }, [onOpenChange]);

  const propertiesQuery = useQuery({
    queryKey: ['search-console-properties', project.id, ticket],
    queryFn: () =>
      integrationApi.listGoogleSearchConsoleProperties(project.id, ticket!),
    enabled: Boolean(open && ticket),
    retry: false,
  });

  React.useEffect(() => {
    if (!siteUrl && propertiesQuery.data) {
      setSiteUrl(
        matchingSearchConsoleSite(project, propertiesQuery.data.sites),
      );
    }
  }, [propertiesQuery.data, project, siteUrl]);

  const authorizeMutation = useMutation({
    mutationFn: () =>
      integrationApi.authorizeGoogleSearchConsole(project.id, returnTo),
    onSuccess: ({ authorizationUrl }) =>
      window.location.assign(authorizationUrl),
    onError: captureError,
  });

  const connectMutation = useMutation({
    mutationFn: () =>
      integrationApi.connectGoogleSearchConsole(project.id, {
        ticket: ticket!,
        siteUrl,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', project.id],
      });
      close();
    },
    onError: captureError,
  });

  const close = () => {
    setTicket(null);
    setOauthError(null);
    setSiteUrl('');
    clearError();
    clearOAuthResult();
    onOpenChange(false);
  };

  const queryError = propertiesQuery.error
    ? propertiesQuery.error instanceof Error
      ? propertiesQuery.error.message
      : 'Could not load Search Console properties.'
    : null;
  const sites = propertiesQuery.data?.sites ?? [];

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            if (ticket) {
              connectMutation.mutate();
            } else {
              authorizeMutation.mutate();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Connect Google Search Console</DialogTitle>
            <DialogDescription>
              Read search queries, pages, clicks, impressions and rankings.
              GainSignal cannot change your site or Search Console settings.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              {error || oauthError || queryError ? (
                <Alert tone="error">{error ?? oauthError ?? queryError}</Alert>
              ) : null}

              {!ticket ? (
                <div className="rounded-lg bg-surface-muted px-3 py-3 text-sm text-ink-muted">
                  Google will ask for one read-only Search Console permission.
                  Afterward, choose which verified property belongs to this
                  project.
                </div>
              ) : propertiesQuery.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : sites.length ? (
                <Field
                  label="Search Console property"
                  htmlFor={`search-console-property-${project.id}`}
                  hint="Domain properties include every protocol and subdomain."
                >
                  <Select value={siteUrl} onValueChange={setSiteUrl}>
                    <SelectTrigger id={`search-console-property-${project.id}`}>
                      <SelectValue placeholder="Choose a property" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.siteUrl} value={site.siteUrl}>
                          {site.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : propertiesQuery.isSuccess ? (
                <div className="rounded-lg bg-surface-muted px-3 py-3 text-sm text-ink-muted">
                  This Google account has no verified Search Console properties.
                </div>
              ) : null}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={authorizeMutation.isPending || connectMutation.isPending}
              disabled={Boolean(ticket && !siteUrl)}
            >
              <Search />
              {ticket ? 'Connect property' : 'Continue with Google'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
